//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { CollectionType } from '@apis/collections/CollectionType';
import { createCollection } from '@apis/collections/collectionsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { useGetCollectionsByTypesQuery } from '@media-library/queries';
import { isGalleryUploadsStream } from '@media-library/utils/mediaLibraryUtils';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { queryClient } from '@queries/ReactQueryProvider';

export const useBulkStreamCollections = () => {
  const dispatch = useDispatch();

  // Request all 3 types to match the sidebar's cache key (avoids a refetch on modal open); filter locally below.
  const { data: allStreams = [], isPending: isLoading } =
    useGetCollectionsByTypesQuery({
      collectionTypes: [
        CollectionType.COLLECTION,
        CollectionType.RECOMMENDATIONS_STREAM,
        CollectionType.STREAM,
      ],
      enabled: true,
    });

  const streams = allStreams.filter(
    (stream) =>
      (stream.collectionType === CollectionType.STREAM ||
        stream.collectionType === CollectionType.COLLECTION) &&
      !isGalleryUploadsStream(stream),
  );

  const { isPending: isCreating, mutateAsync: createStream } = useMutation({
    mutationFn: (name: string) =>
      createCollection({
        collectionType: CollectionType.STREAM,
        name: name.trim(),
      }),
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Failed to create stream',
          type: ToastType.ERROR,
        }),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaLibrary,
          QueryKeyConstants.MediaLibraryCollections,
        ],
      });
    },
  });

  return { createStream, isCreating, isLoading, streams };
};
